import clsx from 'clsx';
import { useRef } from 'react';
import black_plus from '../../../../../../../assets/black-plus.svg';
import styles from './DragDropFileHandler.module.scss';

function DragDropFileHandler({
    onChange,
    disabled = false,
    acceptedFormats,
    allowMultiple,
    onClickFunction,
    variant = 'default',
}: {
    onChange: (files: FileList | File[]) => void;
    disabled?: boolean;
    acceptedFormats?: string | string[] | null;
    allowMultiple?: boolean;
    onClickFunction?: Function;
    variant?: 'default' | 'journey';
}) {
    const fileInputRef = useRef() as React.MutableRefObject<HTMLInputElement>;
    const acceptAttribute = Array.isArray(acceptedFormats) ? acceptedFormats.join(',') : acceptedFormats ?? undefined;
    const handleClick = () => {
        if (disabled) return;
        // Gesture-safe (D1c): open the drawer params THEN synchronously click the input, both
        // inside this one click handler — the picker must open within the same user gesture.
        onClickFunction?.();
        fileInputRef?.current?.click?.();
    };

    const handleChange = (e) => {
        if (variant === 'journey') {
            // SF3: re-selecting the same file fires no change event unless the input is reset.
            // Copy the files before resetting — some browsers invalidate the live FileList on reset.
            const files = e.target.files ? Array.from(e.target.files) : [];
            e.target.value = '';
            onChange(files as File[]);
            return;
        }
        onChange(e.target.files);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onChange(e.dataTransfer.files);
        if (e.dataTransfer) e.dataTransfer.clearData();
        // setDragActive(false);
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
        } else if (e.type === 'dragleave') {
            // setDragActive(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        // Space also scrolls the page by default — stop that, same as a native button.
        e.preventDefault();
        handleClick();
    };

    if (variant === 'journey') {
        return (
            <div
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-label="Select a file or drop here."
                aria-disabled={disabled ? 'true' : undefined}
                className={clsx(styles.JourneyDropzone, {
                    [styles.Disabled]: disabled,
                })}
                onDrop={handleDrop}
                onDragOver={handleDrag}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    onChangeCapture={handleChange}
                    style={{ display: 'none' }}
                    accept={acceptAttribute}
                    multiple={!!allowMultiple}
                />
                <div className={styles.JourneySelect}>Select a file or drop here.</div>
                <img src={black_plus} alt="plus" height="16px" />
            </div>
        );
    }

    return (
        <div
            className={clsx(styles.DragDropFileHandler, {
                [styles.Disabled]: disabled,
            })}
            onDrop={handleDrop}
            onDragOver={handleDrag}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onClick={handleClick}
        >
            <input
                type="file"
                ref={fileInputRef}
                onChangeCapture={handleChange}
                style={{ display: 'none' }}
                accept={acceptAttribute}
                multiple={!!allowMultiple}
            />
            <div className={styles.Left}>
                <div className={styles.Drag}>Drag and Drop</div>
                <div className={styles.Select}>Select a file or drop one here</div>
            </div>
            <div className={styles.Right}>
                <img src={black_plus} alt="plus" height="20px" />
            </div>
        </div>
    );
}

export default DragDropFileHandler;
